import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, Smile, Paperclip, FileText, MessageSquare, ArrowLeft, RefreshCw,
  MoreVertical, Phone, Image as ImageIcon, Video, Mic, File as FileIcon,
  X, Loader2, ChevronDown, Reply as ReplyIcon, CheckCheck, Check,
  Forward as ForwardIcon, Star, Trash2,
} from 'lucide-react';
import { avatarColor, initials, dayLabel, formatSize } from './utils';
import EmojiPicker from './EmojiPicker';
import MessageBubble from './MessageBubble';
import ForwardPicker from './ForwardPicker';
import { hydrateActions, toggleStar, markDeleted, stripForwardMarker } from './actions';

// ───────────────────────── Small bits ─────────────────────────

const DaySeparator = ({ iso }) => (
  <div className="flex justify-center my-4 select-none">
    <span className="text-[11px] font-medium text-gray-600 bg-white/90 backdrop-blur px-3 py-1 rounded-full shadow-sm border border-gray-100">
      {dayLabel(iso)}
    </span>
  </div>
);

// ───────────────────────── Composer ─────────────────────────

const Composer = ({
  onSend, sending, onAttach, templates, onTemplatePick, fileInputRef,
}) => {
  const [value, setValue] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const taRef = useRef(null);

  const autogrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  };

  const handleSend = () => {
    const v = value.trim();
    if (!v || sending) return;
    onSend(v);
    setValue('');
    requestAnimationFrame(autogrow);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative bg-white border-t border-gray-200 px-3 py-2.5" data-testid="inbox-composer">
      {showEmoji && (
        <EmojiPicker
          onPick={(e) => { setValue(v => v + e); requestAnimationFrame(autogrow); taRef.current?.focus(); }}
          onClose={() => setShowEmoji(false)}
        />
      )}

      {showTemplates && (
        <div className="absolute bottom-14 left-2 z-40 w-80 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl"
          data-testid="inbox-template-picker">
          <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 sticky top-0 bg-white">
            Message Templates
          </div>
          {(templates || []).length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">No templates available</div>
          ) : templates.map(t => (
            <button key={t.id}
              onClick={() => {
                const body = t.body || t.content || '';
                setValue(v => (v ? v + '\n' : '') + body);
                onTemplatePick?.(t);
                setShowTemplates(false);
                requestAnimationFrame(autogrow);
                taRef.current?.focus();
              }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
              data-testid={`inbox-template-${t.id}`}>
              <div className="text-sm font-medium text-gray-800 truncate">{t.name || 'Untitled'}</div>
              <div className="text-xs text-gray-500 truncate">{t.body || t.content || ''}</div>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-1.5">
        <button
          type="button"
          onClick={() => { setShowEmoji(s => !s); setShowTemplates(false); }}
          className={`p-2 rounded-full transition-colors ${showEmoji ? 'bg-emerald-50 text-emerald-600' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Emoji"
          data-testid="inbox-emoji-btn"
        >
          <Smile size={20} />
        </button>

        <button
          type="button"
          onClick={() => onAttach()}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
          title="Attach file"
          data-testid="inbox-attach-btn"
        >
          <Paperclip size={20} />
        </button>

        <button
          type="button"
          onClick={() => { setShowTemplates(s => !s); setShowEmoji(false); }}
          className={`p-2 rounded-full transition-colors ${showTemplates ? 'bg-emerald-50 text-emerald-600' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Templates"
          data-testid="inbox-template-btn"
        >
          <FileText size={20} />
        </button>

        <div className="flex-1 bg-gray-100 rounded-2xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500/40 focus-within:bg-white transition-all">
          <textarea
            ref={taRef}
            rows={1}
            value={value}
            onChange={(e) => { setValue(e.target.value); autogrow(); }}
            onKeyDown={onKey}
            placeholder="Type a message"
            className="w-full resize-none bg-transparent outline-none text-sm leading-6 placeholder-gray-400 max-h-[140px]"
            data-testid="inbox-message-input"
          />
        </div>

        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !value.trim()}
          className="p-2.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-all"
          title="Send"
          data-testid="inbox-send-btn"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
};

// ───────────────────────── Main window ─────────────────────────

const ChatWindow = ({
  selectedChat, contactInfo, messages, loadingMessages, loadingOlder, hasMore,
  onLoadOlder, onSend, sending, onBack, onRefresh, onToggleProfile,
  fileInputRef, onAttach, templates, onRetry, onForwardMessages, chats,
  uploadOpen, mediaPreview, onUpload, onCancelUpload, uploading,
}) => {
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const [stickToBottom, setStickToBottom] = useState(true);
  const [actionTick, setActionTick] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardItems, setForwardItems] = useState(null); // null | Msg[]
  const [infoMsg, setInfoMsg] = useState(null);
  const [forwarding, setForwarding] = useState(false);

  // Multi-select mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const toggleSelect = (id, enterMode = false) => {
    if (enterMode) setSelectMode(true);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Render groups with day separators and media grouping.
  //
  // Media grouping rules: consecutive image/video messages from the same
  // direction sent within 60 seconds of each other collapse into one gallery
  // bubble. Text messages and other types break the group.
  const grouped = useMemo(() => {
    const hydrated = hydrateActions(messages).filter(m => !m.__ui?.deleted);
    const out = [];
    let lastDay = null;
    let mediaBatch = null; // { direction, items:[], key, firstMsg }

    const flushBatch = () => {
      if (mediaBatch) {
        out.push({ kind: mediaBatch.items.length > 1 ? 'group' : 'msg',
          items: mediaBatch.items, msg: mediaBatch.firstMsg,
          key: mediaBatch.key });
        mediaBatch = null;
      }
    };

    for (const m of hydrated) {
      const day = new Date(m.createdAt || Date.now()).toDateString();
      if (day !== lastDay) {
        flushBatch();
        out.push({ kind: 'sep', iso: m.createdAt, key: `sep-${day}` });
        lastDay = day;
      }
      const type = m.messageType || m.type;
      const isGroupable = (type === 'image' || type === 'video') && (m.mediaUrl || m.media_url);

      if (isGroupable && mediaBatch
          && mediaBatch.direction === m.direction
          && (new Date(m.createdAt).getTime() - new Date(mediaBatch.lastAt).getTime()) <= 60_000) {
        mediaBatch.items.push(m);
        mediaBatch.lastAt = m.createdAt;
      } else {
        flushBatch();
        if (isGroupable) {
          mediaBatch = {
            direction: m.direction,
            items: [m],
            firstMsg: m,
            lastAt: m.createdAt,
            key: `grp-${m.id || m.createdAt}`,
          };
        } else {
          out.push({ kind: 'msg', msg: m, items: [m], key: m.id || `${m.createdAt}-${Math.random()}` });
        }
      }
    }
    flushBatch();
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, actionTick]);

  const handleStateChange = () => setActionTick(t => t + 1);

  // Auto-scroll to bottom when new messages arrive (if user is near bottom)
  useEffect(() => {
    if (stickToBottom) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, stickToBottom]);

  // On chat change, jump to bottom instantly
  useEffect(() => {
    setStickToBottom(true);
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'auto' }));
  }, [selectedChat?.phone]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    setStickToBottom(nearBottom);
    // Infinite load older when scrolled to top
    if (el.scrollTop < 80 && hasMore && !loadingOlder && !loadingMessages) {
      const prevHeight = el.scrollHeight;
      onLoadOlder?.().then(() => {
        requestAnimationFrame(() => {
          const diff = el.scrollHeight - prevHeight;
          el.scrollTop = diff + el.scrollTop;
        });
      });
    }
  };

  if (!selectedChat) {
    return (
      <section className="hidden md:flex flex-1 items-center justify-center bg-[radial-gradient(#e4e4e7_1px,transparent_1px)] [background-size:22px_22px] bg-[#f7f6f2]">
        <div className="text-center max-w-sm px-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-white shadow-md flex items-center justify-center">
            <MessageSquare size={36} className="text-emerald-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Your Inbox, unified.</h3>
          <p className="text-sm text-gray-500">Select a chat on the left to view the conversation, lead profile and quick actions.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex-1 flex flex-col bg-[#f7f6f2] min-w-0" data-testid="inbox-chat-window">
      {/* Header */}
      <div className="px-3 md:px-4 py-2.5 bg-white border-b border-gray-200 flex items-center gap-3 shadow-sm z-10">
        <button onClick={onBack} className="md:hidden p-1.5 rounded-lg hover:bg-gray-100" data-testid="inbox-back-btn">
          <ArrowLeft size={18} />
        </button>
        <button onClick={onToggleProfile} className="flex items-center gap-3 min-w-0 flex-1 text-left">
          <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${avatarColor(selectedChat.phone)}`}>
            {initials(selectedChat.recipientName, selectedChat.phone)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 truncate">{selectedChat.recipientName || selectedChat.phone}</h3>
              {contactInfo && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${contactInfo.type === 'lead' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {contactInfo.type}
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
              <Phone size={10} /> {selectedChat.phone}
            </p>
          </div>
        </button>
        <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Refresh" data-testid="inbox-chat-refresh">
          <RefreshCw size={16} className={loadingMessages ? 'animate-spin' : ''} />
        </button>
        <button onClick={onToggleProfile} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" title="Profile" data-testid="inbox-toggle-profile">
          <MoreVertical size={16} />
        </button>
      </div>

      {/* Multi-select toolbar */}
      {selectMode && (
        <div className="px-3 md:px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2 animate-in slide-in-from-top-1" data-testid="inbox-select-toolbar">
          <button onClick={exitSelectMode} className="p-1.5 rounded-lg hover:bg-white text-gray-600" data-testid="inbox-select-cancel">
            <X size={16} />
          </button>
          <span className="flex-1 text-sm font-medium text-gray-800" data-testid="inbox-select-count">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => {
              const selMsgs = messages.filter(m => selectedIds.has(m.id));
              if (selMsgs.length) setForwardItems(selMsgs);
            }}
            disabled={!selectedIds.size}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40"
            data-testid="inbox-select-forward"
          >
            <ForwardIcon size={13} /> Forward
          </button>
          <button
            onClick={() => {
              selectedIds.forEach(id => toggleStar(id));
              setActionTick(t => t + 1);
              exitSelectMode();
            }}
            disabled={!selectedIds.size}
            className="p-1.5 rounded-lg hover:bg-white text-amber-600 disabled:opacity-40"
            title="Star selected"
            data-testid="inbox-select-star"
          >
            <Star size={16} />
          </button>
          <button
            onClick={() => {
              selectedIds.forEach(id => markDeleted(id));
              setActionTick(t => t + 1);
              exitSelectMode();
            }}
            disabled={!selectedIds.size}
            className="p-1.5 rounded-lg hover:bg-white text-red-600 disabled:opacity-40"
            title="Delete selected"
            data-testid="inbox-select-delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto px-3 md:px-8 py-4 [scroll-behavior:smooth]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)', backgroundSize: '20px 20px' }}
        data-testid="inbox-messages-scroll"
      >
        {loadingOlder && (
          <div className="flex justify-center py-2">
            <Loader2 className="animate-spin text-gray-400" size={16} />
          </div>
        )}
        {loadingMessages && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="animate-spin text-emerald-500" size={22} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-10">No messages yet. Start the conversation below.</div>
        ) : (
          grouped.map(item => {
            if (item.kind === 'sep') return <DaySeparator key={item.key} iso={item.iso} />;
            const msg = item.msg;
            const mine = msg.direction === 'sent';
            const groupItems = item.kind === 'group' ? item.items : null;
            return (
              <div key={item.key} className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-2`}>
                <MessageBubble
                  msg={msg}
                  mine={mine}
                  groupItems={groupItems}
                  onRetry={onRetry}
                  onReply={setReplyTo}
                  onForward={(items) => setForwardItems(items)}
                  onInfo={setInfoMsg}
                  onStateChange={handleStateChange}
                  onSelect={toggleSelect}
                  selected={selectedIds.has(msg.id)}
                  selectMode={selectMode}
                />
              </div>
            );
          })
        )}
        <div ref={endRef} />

        {!stickToBottom && (
          <button
            onClick={() => endRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="fixed bottom-24 right-[22%] md:right-[24%] lg:right-[26%] w-9 h-9 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
            title="Scroll to latest"
          >
            <ChevronDown size={18} />
          </button>
        )}
      </div>

      {/* Hidden file input is rendered by parent (WhatsAppInbox) */}

      {/* Reply preview above composer */}
      {replyTo && (
        <div className="px-3 pt-2 pb-1 bg-white border-t border-gray-100" data-testid="inbox-reply-preview">
          <div className="flex items-start gap-2 bg-gray-50 border-l-4 border-emerald-500 rounded-md px-3 py-2">
            <ReplyIcon size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-emerald-700">
                {replyTo.direction === 'sent' ? 'You' : (replyTo.recipientName || 'Contact')}
              </p>
              <p className="text-xs text-gray-600 truncate">{replyTo.message || replyTo.caption || '[media]'}</p>
            </div>
            <button onClick={() => setReplyTo(null)} className="p-1 rounded hover:bg-gray-200 text-gray-500" data-testid="inbox-reply-close">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Composer */}
      <Composer
        onSend={(text) => {
          const prefix = replyTo ? `↪ ${(replyTo.message || replyTo.caption || '[media]').slice(0, 80)}\n\n` : '';
          onSend(prefix + text);
          setReplyTo(null);
        }}
        sending={sending}
        onAttach={onAttach}
        templates={templates}
        fileInputRef={fileInputRef}
      />

      {/* Media upload preview modal */}
      {uploadOpen && mediaPreview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="inbox-media-preview">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                {mediaPreview.type === 'image' && <ImageIcon size={16} className="text-blue-500" />}
                {mediaPreview.type === 'video' && <Video size={16} className="text-purple-500" />}
                {mediaPreview.type === 'audio' && <Mic size={16} className="text-emerald-500" />}
                {mediaPreview.type === 'document' && <FileIcon size={16} className="text-orange-500" />}
                Send {mediaPreview.type}
              </h3>
              <button onClick={onCancelUpload} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-4">
              {mediaPreview.type === 'image' && <img src={mediaPreview.preview} alt="" className="max-h-80 mx-auto rounded-lg object-contain" />}
              {mediaPreview.type === 'video' && <video src={mediaPreview.preview} controls className="max-h-80 mx-auto rounded-lg" />}
              {(mediaPreview.type === 'audio' || mediaPreview.type === 'document') && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  {mediaPreview.type === 'audio' ? <Mic size={28} className="text-emerald-500" /> : <FileText size={28} className="text-red-500" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{mediaPreview.name}</p>
                    <p className="text-xs text-gray-500">{formatSize(mediaPreview.size)}</p>
                  </div>
                </div>
              )}
              <input id="inbox-media-caption" type="text" placeholder="Add a caption..."
                className="mt-4 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid="inbox-media-caption" />
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={onCancelUpload} className="px-4 py-2 text-sm text-gray-600 hover:bg-white rounded-lg">Cancel</button>
              <button
                onClick={() => onUpload(document.getElementById('inbox-media-caption')?.value || '')}
                disabled={uploading}
                className="px-4 py-2 text-sm bg-emerald-500 text-white font-medium rounded-lg hover:bg-emerald-600 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="inbox-media-send-btn"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Info modal */}
      {infoMsg && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setInfoMsg(null)} data-testid="inbox-info-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-sm">Message Info</h3>
              <button onClick={() => setInfoMsg(null)} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="p-2.5 rounded-lg bg-gray-50 text-gray-700 text-sm break-words">
                {infoMsg.message || infoMsg.caption || `[${infoMsg.messageType || 'media'}]`}
              </div>
              {[
                { label: 'Sent', icon: Check, iso: infoMsg.sentAt || infoMsg.createdAt, color: 'text-gray-500' },
                { label: 'Delivered', icon: CheckCheck, iso: infoMsg.deliveredAt, color: 'text-gray-500' },
                { label: 'Read', icon: CheckCheck, iso: infoMsg.readAt, color: 'text-sky-500' },
                ...(infoMsg.failedAt || infoMsg.status === 'failed' ? [{ label: 'Failed', icon: X, iso: infoMsg.failedAt || infoMsg.createdAt, color: 'text-red-500', extra: infoMsg.error }] : []),
              ].map(r => r.iso || r.extra ? (
                <div key={r.label} className="flex items-start gap-2">
                  <r.icon size={14} className={`${r.color} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">{r.label}</p>
                    {r.iso && <p className="text-sm text-gray-800">{new Date(r.iso).toLocaleString()}</p>}
                    {r.extra && <p className="text-xs text-red-600 mt-0.5 break-words">{r.extra}</p>}
                  </div>
                </div>
              ) : null)}
              {infoMsg.retryCount > 0 && (
                <div className="text-[11px] text-gray-500">Retries: {infoMsg.retryCount}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Real forward flow — chat picker */}
      <ForwardPicker
        open={!!forwardItems}
        onClose={() => { setForwardItems(null); setForwarding(false); }}
        chats={chats || []}
        messages={forwardItems || []}
        sending={forwarding}
        onConfirm={async (targetPhones) => {
          if (!forwardItems?.length || !targetPhones?.length) return;
          setForwarding(true);
          try {
            await onForwardMessages?.(forwardItems, targetPhones);
            setForwardItems(null);
            exitSelectMode();
          } finally {
            setForwarding(false);
          }
        }}
      />
    </section>
  );
};

export default ChatWindow;
